import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import { getUserName } from '../utils/userUtils';

export default function GroceryList({ users = [] }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newItemName, setNewItemName] = useState("");
  const [newCategory, setNewCategory] = useState("Snacks");
  const [shopperNameInput, setShopperNameInput] = useState("");
  const [claimingItemId, setClaimingItemId] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    // Subscribe to grocery updates
    const unsubscribe = subscribeToCollection("groceries", (data) => {
      // Sort: Active items first, then purchased. Inside, sorted by category.
      const sorted = [...data].sort((a, b) => {
        if (a.purchased !== b.purchased) return a.purchased ? 1 : -1;
        return a.category.localeCompare(b.category);
      });
      setItems(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemName) return;

    const addedBy = localStorage.getItem("prine_user_name") || "Guest";
    const newItem = {
      id: `groc_${Date.now()}`,
      name: newItemName.trim(),
      category: newCategory,
      addedBy,
      shopper: "",
      purchased: false
    };

    await saveDocument("groceries", newItem.id, newItem);
    setNewItemName("");
    // Note: No localStorage write here as requested to avoid side-effects
  };

  const handleTogglePurchased = async (item) => {
    const updated = {
      ...item,
      purchased: !item.purchased
    };
    await saveDocument("groceries", item.id, updated);
  };

  const handleStartClaim = (itemId) => {
    setClaimingItemId(itemId);
    const savedName = localStorage.getItem("prine_user_name") || "";
    setShopperNameInput(savedName);
  };

  const handleConfirmClaim = async (item) => {
    if (!shopperNameInput) return alert("Please select your name to claim.");

    const updated = {
      ...item,
      shopper: shopperNameInput
    };

    await saveDocument("groceries", item.id, updated);
    setClaimingItemId(null);
    // Note: No localStorage shopper_name write here to avoid side-effects
  };

  const handleUnclaim = async (item) => {
    if (!window.confirm(`Are you sure you want to remove ${getUserName(item.shopper)} as the shopper for this item?`)) return;
    const updated = {
      ...item,
      shopper: ""
    };
    await saveDocument("groceries", item.id, updated);
  };

  const handleDeleteGrocery = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
    await deleteDocument("groceries", item.id);
  };

  const categories = ["Snacks", "Drinks", "Breakfast", "Dinner", "Other"];

  // Re-organize items programmatically
  const activeItems = items.filter(item => !item.purchased);
  const boughtItems = items.filter(item => item.purchased);
  const activeCategories = Array.from(new Set(activeItems.map(i => i.category))).sort((a, b) => a.localeCompare(b));

  return (
    <div className="tab-panel">
      <div className="view-title-container">
        <div className="view-title">
          <h2>🛒 Grocery List</h2>
        </div>
      </div>

      <div className="grocery-layout">
        {/* Left Side: Add Item Form & Splitwise Panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="glass-panel" style={{ padding: '24px' }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.2rem', marginBottom: '16px', color: 'var(--color-text-main)' }}>Add Grocery Item</h3>
            <form onSubmit={handleAddItem} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-group">
                <label>Item Name</label>
                <input
                  type="text"
                  placeholder="e.g. Tortilla Chips"
                  className="input-field"
                  value={newItemName}
                  onChange={e => setNewItemName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  className="input-field"
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                >
                  {categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}>
                Add to Shared List
              </button>
            </form>
          </div>

          {/* Submit Costs Panel */}
          <div className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.1rem', margin: 0, color: 'var(--color-text-main)' }}>Grocery Submission</h3>

            <a
              href="https://www.splitwise.com/join/eXygDyybKjH+wf34v?v=w"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                textDecoration: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '0.85rem',
                fontWeight: '700'
              }}
            >
              Submit Costs
            </a>

            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: '1.5', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                <span>💡</span>
                <span>Submit your own grocery bills or anyone else's.</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                <span>🏠</span>
                <span>Households can portion different shares of the cost.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Grocery Items Grid */}
        <div className="grocery-list-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-chip">
                <span className="spinner"></span>
                <span>Syncing Grocery Board...</span>
              </div>
            </div>
          ) : activeItems.length === 0 && boughtItems.length === 0 ? (
            <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Grocery list is empty. Time to sit back and relax!
            </div>
          ) : (
            <>
              {/* Active Items Grouped by Category */}
              {activeCategories.map(category => {
                const categoryItems = activeItems.filter(i => i.category === category);
                return (
                  <div key={category} className="grocery-category-section" style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontFamily: 'var(--font-title)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: 'var(--color-primary)',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid var(--color-glass-border)',
                      paddingBottom: '4px'
                    }}>
                      {category} ({categoryItems.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {categoryItems.map(item => (
                        <div
                          key={item.id}
                          className={`grocery-item-card ${item.shopper ? 'claimed' : ''}`}
                        >
                          <div className="grocery-item-left">
                            {/* Custom Checkbox */}
                            <div
                              className="grocery-checkbox"
                              onClick={() => handleTogglePurchased(item)}
                            />

                            <div className="grocery-text-block">
                              <span className="grocery-name">
                                {item.name}
                              </span>
                              <div className="grocery-metadata">
                                Added by: <span>{getUserName(users, item.addedBy)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="grocery-item-actions">
                            {claimingItemId === item.id ? (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <select
                                  className="input-field"
                                  value={shopperNameInput}
                                  onChange={e => setShopperNameInput(e.target.value)}
                                  style={{ padding: '4px 10px', fontSize: '0.8rem', width: '130px' }}
                                >
                                  <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select --</option>
                                  {users.map(u => (
                                    <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                                      {u.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  className="btn-claim"
                                  style={{ padding: '4px 10px' }}
                                  onClick={() => handleConfirmClaim(item)}
                                >
                                  Claim
                                </button>
                              </div>
                            ) : item.shopper ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="grocery-shopper-tag">
                                  Shopper: {getUserName(users, item.shopper)}
                                  <button
                                    className="btn-inline-delete-cross"
                                    style={{ padding: '0 0 0 4px', lineHeight: 1 }}
                                    onClick={() => handleUnclaim(item)}
                                    title="Unassign shopper"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button className="btn-claim" onClick={() => handleStartClaim(item.id)}>
                                  Assign Shopper
                                </button>
                                <button
                                  type="button"
                                  className="btn-inline-delete-cross"
                                  style={{ fontSize: '1.25rem', lineHeight: 1 }}
                                  onClick={() => handleDeleteGrocery(item)}
                                  title="Delete item"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Bought Items Section at the Very Bottom */}
              {boughtItems.length > 0 && (
                <div className="grocery-category-section" style={{ marginTop: '24px' }}>
                  <h4 style={{
                    fontFamily: 'var(--font-title)',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'var(--color-text-muted)',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--color-glass-border)',
                    paddingBottom: '4px'
                  }}>
                    Bought ({boughtItems.length})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {boughtItems.map(item => (
                      <div
                        key={item.id}
                        className="grocery-item-card claimed purchased"
                        style={{ opacity: 0.6 }}
                      >
                        <div className="grocery-item-left">
                          <div
                            className="grocery-checkbox checked"
                            onClick={() => handleTogglePurchased(item)}
                          />
                          <div className="grocery-text-block">
                            <span className="grocery-name" style={{ textDecoration: 'line-through' }}>
                              {item.name}
                            </span>
                            <div className="grocery-metadata">
                              Added by: <span>{getUserName(users, item.addedBy)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="grocery-item-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="grocery-shopper-tag" style={{ background: 'rgba(148, 163, 184, 0.1)', color: 'var(--color-text-muted)', borderColor: 'transparent' }}>
                            Bought {item.shopper ? `by ${getUserName(users, item.shopper)}` : ""}
                          </span>
                          {!item.shopper && (
                            <button
                              type="button"
                              className="btn-inline-delete-cross"
                              style={{ fontSize: '1.25rem', lineHeight: 1 }}
                              onClick={() => handleDeleteGrocery(item)}
                              title="Delete item"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
